import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { format as formatDate } from 'date-fns';
import { Store } from '@ngrx/store';
import { System, EvChDataV2, isNotNullOrUndefined, PlantEmsEnumType, ProductModelService } from 'ngx-core-services';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { EChartsOption } from 'echarts';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import {
  SystemsState,
  selectedSystemUid,
  selectEvchargeEnergyAggregationMonthlyData,
  selectEvchargeEnergyAggregationYearlyData,
  selectEvchargeEnergyAggregationMonthlyState,
  selectEvchargeEnergyAggregationYearlyState,
  requestSystemEvchargeEnergyAggregation,
  selectedSystem,
} from '../../store';
import { AggregatedEnergyData, DailyAggregation } from './evcharge-energy-aggregation.model';
import { EvchargeSessionChartService } from '../services/evcharge-session-chart.service';

const getLabels = () => ({
  title: 'Energy Aggregation',
  totalEnergy: 'Total Energy',
  totalSessions: 'Total Sessions',
  avgSessionEnergy: 'Avg. Session Energy',
  month: 'Month',
  year: 'Year',
  noDataForTheSelectedPeriod: 'No data available for the selected period',
  energy: 'Energy',
  sessions: 'Sessions',
  date: 'Date',
});

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-640': 640,
})
@Component({
  selector: 'lib-evcharge-energy-aggregation',
  templateUrl: './evcharge-energy-aggregation.component.html',
  styleUrls: ['./evcharge-energy-aggregation.component.scss'],
})
export class EvchargeEnergyAggregationComponent implements ICustomBlock, IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly periodSelection$ = new BehaviorSubject<'month' | 'year'>('month');
  readonly dateSelection$ = new BehaviorSubject<Date>(new Date());

  readonly requestData$ = combineLatest([
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this.periodSelection$,
    this.dateSelection$,
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([deviceId, systemId, period, date, forceRefresh, instanceId]) => {
      this.store.dispatch(requestSystemEvchargeEnergyAggregation({
        systemId,
        instanceId,
        deviceId,
        period,
        date,
        forceRefresh
      }));
    }),
  );

  readonly monthlyData$ = this._instanceId$.pipe(
    switchMap((instanceId) =>
      combineLatest([
        of(instanceId),
        this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined())
      ])
    ),
    switchMap(([instanceId, systemId]) =>
      this.store.select(selectEvchargeEnergyAggregationMonthlyData(instanceId, systemId))
    ),
  );

  readonly yearlyData$ = this._instanceId$.pipe(
    switchMap((instanceId) =>
      combineLatest([
        of(instanceId),
        this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined())
      ])
    ),
    switchMap(([instanceId, systemId]) =>
      this.store.select(selectEvchargeEnergyAggregationYearlyData(instanceId, systemId))
    ),
  );

  readonly monthlyState$ = this._instanceId$.pipe(
    switchMap((instanceId) =>
      combineLatest([
        of(instanceId),
        this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined())
      ])
    ),
    switchMap(([instanceId, systemId]) =>
      this.store.select(selectEvchargeEnergyAggregationMonthlyState(instanceId, systemId))
    ),
  );

  readonly yearlyState$ = this._instanceId$.pipe(
    switchMap((instanceId) =>
      combineLatest([
        of(instanceId),
        this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined())
      ])
    ),
    switchMap(([instanceId, systemId]) =>
      this.store.select(selectEvchargeEnergyAggregationYearlyState(instanceId, systemId))
    ),
  );

  readonly requestIsPending$ = combineLatest([
    this.periodSelection$,
    this.monthlyState$.pipe(startWith('pending')),
    this.yearlyState$.pipe(startWith('pending')),
  ]).pipe(
    map(([period, monthlyState, yearlyState]) =>
      period === 'month' ? monthlyState === 'pending' : yearlyState === 'pending'
    ),
  );

  readonly aggregatedData$: Observable<AggregatedEnergyData | null> = combineLatest([
    this.periodSelection$,
    this.dateSelection$,
    this.monthlyData$.pipe(startWith(null)),
    this.yearlyData$.pipe(startWith(null)),
    this.store.select(selectedSystem).pipe(isNotNullOrUndefined())
  ]).pipe(
    map(([period, selectedDate, monthlyData, yearlyData, selectedSystem]) => {
      const rawData = period === 'month' ? monthlyData : yearlyData;
      if (!rawData || rawData.length === 0) return null;

      return this.transformToAggregatedData(rawData, period, selectedDate);
    }),
  );

  readonly chartOptions$: Observable<EChartsOption | undefined> = combineLatest([
    this.aggregatedData$,
    this.requestIsPending$
  ]).pipe(
    map(([data, isPending]) => {
      if (isPending || !data) return undefined;

      return this.chartService.buildEnergyAggregationChart(data, {
        energy: this.labels.energy,
        sessions: this.labels.sessions,
        date: this.labels.date
      });
    }),
  );

  readonly reducedChartOptions$: Observable<EChartsOption[] | undefined> = combineLatest([
    this.aggregatedData$,
    this.requestIsPending$
  ]).pipe(
    map(([data, isPending]) => {
      if (isPending || !data) return undefined;

      return this.chartService.buildReducedEnergyAggregationCharts(data, {
        energy: this.labels.energy,
        sessions: this.labels.sessions,
        date: this.labels.date
      });
    }),
  );

  constructor(
    public readonly el: ElementRef,
    public readonly renderer: Renderer2,
    public readonly store: Store<SystemsState>,
    private readonly chartService: EvchargeSessionChartService
  ) {}

  onPeriodChange(period: any): void {
    this.periodSelection$.next(period as 'month' | 'year');
  }

  onDateSelected(date: string): void {
    this.dateSelection$.next(new Date(date));
  }

  private transformToAggregatedData(rawData: EvChDataV2[], period: 'month' | 'year', selectedDate: Date): AggregatedEnergyData {
    // Create a map of existing data by date
    const dataMap = new Map<string, DailyAggregation>();
    rawData.forEach((item: any) => {
      const date = item.SK || formatDate(new Date(item.TS * 1000), 'yyyy-MM-dd');

      /**
       * For 'EvChDAYV2' [Connext], DEM is in Wh, convert to kWh by dividing by 1000.
       */
      const dailyEnergyMeter = item.__typename === 'EvChDAYV2'
        ? parseFloat((((item.DEM || 0) / 1000) % 1 === 0 ? (item.DEM || 0) / 1000 : ((item.DEM || 0) / 1000).toFixed(2)).toString())
        : (item.DEM || 0);

      if (period === 'year') {
        // For yearly aggregation, sum all data for the same month
        const yearMonth = date.substring(0, 7); // "2025-01"
        const existing = dataMap.get(yearMonth);

        if (existing) {
          existing.totalEnergy += (dailyEnergyMeter || 0);
          existing.sessionCount += (item.CC || 0);
        } else {
          dataMap.set(yearMonth, {
            date: yearMonth + '-01',
            totalEnergy: (dailyEnergyMeter || 0),
            sessionCount: item.CC || 0,
            rawData: item
          });
        }
      } else {
        dataMap.set(date, {
          date,
          totalEnergy: (dailyEnergyMeter || 0),
          sessionCount: item.CC || 0,
          rawData: item
        });
      }
    });

    // Calculate start and end dates based on period and selected date
    let startDate: Date;
    let endDate: Date;

    if (period === 'month') {
      // First day of the month to last day of the month
      startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      endDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    } else {
      // January 1st to December 31st of the selected year
      startDate = new Date(selectedDate.getFullYear(), 0, 1);
      endDate = new Date(selectedDate.getFullYear(), 11, 31);
    }

    // Generate all dates in the period
    const allDates: string[] = [];
    const to = new Date(endDate);
    to.setHours(23, 59, 59, 999);

    for (let d = new Date(startDate); d <= to; ) {
      allDates.push(formatDate(d, 'yyyy-MM-dd'));
      if (period === 'year') {
        d.setMonth(d.getMonth() + 1);
      } else {
        d.setDate(d.getDate() + 1);
      }
    }

    // Create dailyData with all dates, filling missing ones with zeros
    const dailyData: DailyAggregation[] = allDates.map(date => {
      if (period === 'year') {
        // For yearly data, match by year-month only
        const yearMonth = date.substring(0, 7); // "2025-01"
        return dataMap.get(yearMonth) || {
          date,
          totalEnergy: 0,
          sessionCount: 0,
          rawData: null
        };
      }

      return dataMap.get(date) || {
        date,
        totalEnergy: 0,
        sessionCount: 0,
        rawData: null
      };
    });

    const totalEnergy = dailyData.reduce((sum, d) => sum + d.totalEnergy, 0);
    const totalSessions = dailyData.reduce((sum, d) => sum + d.sessionCount, 0);

    return {
      period,
      startDate,
      endDate,
      totalEnergy,
      totalSessions,
      avgSessionEnergy: totalSessions > 0 ? totalEnergy / totalSessions : 0,
      dailyData,
      dataloggerType: (rawData[0] as any).__typename || 'EvChDAYV2',
      hasData: dailyData.some(d => d.totalEnergy > 0 || d.sessionCount > 0)
    };
  }
}
